import React, { useEffect, useState } from 'react';
import AIContent from '../../../Common/AIContent';
import { fetchItems, patchItem } from '../../../../support/queries';
import { fetchOrCreateAIContent, createAIResponse } from '../../../../support/aiHelpers';
import { HbrSpinner, HbrCard, HbrSelect, HbrOption, HbrTextarea } from '@harbor/elements-react-standalone';
import Typography from '@mui/material/Typography';
import {
  useFetchOpportunity,
  useFetchOpportunityProducts,
  useUpdateOpportunityMutation,
} from '../../../../hooks/opportunity-hooks';
import { useFetchPov } from '../../opportunityDetailHelpers';
import { useParams } from 'react-router-dom';
import { OpportunityPhase, OpportunitySEStatus } from '../../../../types/opportunity';
import { updateSENote } from '../../../../support/salesForceHelpers';
import StbButton from '../../../security-toolbox-ui/StbButton';
import { useScreenSize } from '../../../../hooks/useScreenSize';
import { asSingleValue } from '../../../../support/utils';
import { RootState } from '../../../../support/store';
import { useSelector } from 'react-redux';

const closeStatusOptions: { value: string; label: string }[] = [
  { value: 'Won', label: 'Won' },
  { value: 'Lost', label: 'Lost' },
];

const TechClosePhase: React.FC = () => {
  const { id: opportunityId } = useParams();
  const { email } = useSelector((state: RootState) => state.user.userData);

  const { data: opportunity } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });
  const { mutateAsync: updateOpportunity } = useUpdateOpportunityMutation();

  const [guide, setGuide] = useState<any>(null);
  const [aiContextText, setAiContextText] = useState('');
  const [aiResponse, setAIResponse] = useState<any>(null);
  const [aiLoading, setAILoading] = useState(false);
  const [aiError, setAIError] = useState<string | null>(null);
  const [closeStatus, setCloseStatus] = useState('Won');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forceFormView, setForceFormView] = useState(false);

  const { data: pov } = useFetchPov(opportunity);
  const { data: products } = useFetchOpportunityProducts(opportunityId, { enabled: !!opportunityId });

  const { height: viewportHeight } = useScreenSize();

  const TOP_OFFSET_PX = 510;
  const techCloseBoxHeightPx = Math.max(320, viewportHeight - TOP_OFFSET_PX);
  const techCloseBoxHeight = `${techCloseBoxHeightPx}px`;
  const aiContentHeightPx = Math.max(320, viewportHeight - TOP_OFFSET_PX);
  const aiContentHeight = `${aiContentHeightPx}px`;

  const shouldShowForm =
    forceFormView ||
    opportunity?.se_phase === OpportunityPhase.TechClose ||
    !opportunity?.se_phase;

  const canShowExistingAI =
    opportunity?.se_phase === OpportunityPhase.TechCloseWon ||
    opportunity?.se_phase === OpportunityPhase.TechCloseLost;

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        if (pov) {
          const povOutcomes = await fetchItems('db/pov-outcome', { pov: pov.id });
          const enrichedOutcomes = await Promise.all(
            povOutcomes.map(async (outcome) => {
              const povUseCases = await fetchItems('db/pov-usecase', { pov_outcome: outcome.id });
              const useCases = await Promise.all(
                povUseCases.map(async (useCase) => {
                  const testCases = await fetchItems('db/pov-test-case', { pov_usecase: useCase.id });
                  return { ...useCase, testCases };
                })
              );
              return { ...outcome, useCases };
            })
          );

          setGuide({
            id: `guide-${pov.id}`,
            title: pov.name,
            outcomes: enrichedOutcomes,
            povId: pov.id,
          });
        }
      } catch (error) {
        console.error('Error fetching or creating guides:', error);
      }
    };

    fetchGuides();
  }, [pov]);

  useEffect(() => {
    if (!opportunity?.se_phase) return;

    if (opportunity.se_phase === OpportunityPhase.TechCloseWon) {
      setCloseStatus('Won');
    } else if (opportunity.se_phase === OpportunityPhase.TechCloseLost) {
      setCloseStatus('Lost');
    } else if (opportunity.se_phase === OpportunityPhase.TechClose) {
      setCloseStatus('Won');
    }
  }, [opportunity?.se_phase]);

  useEffect(() => {
    if (shouldShowForm) {
      setAIResponse(null);
      setAiContextText('');
      setAILoading(false);
      setAIError(null);
      return;
    }

    const fetchExistingAI = async () => {
      if (!opportunity || !canShowExistingAI) return;

      setAILoading(true);
      setAIError(null);

      try {
        const chat = await fetchOrCreateAIContent(
          'opportunity',
          opportunityId,
          104,
          '',
          '',
          [],
          opportunity
        );

        if (chat && typeof chat === 'object' && chat.id && chat.response) {
          setAIResponse(chat);
        } else {
          setAIResponse(null);
        }
      } catch (err) {
        setAIError('Failed to fetch AI response.');
        setAIResponse(null);
      } finally {
        setAILoading(false);
      }
    };

    fetchExistingAI();
  }, [opportunityId, opportunity, shouldShowForm, canShowExistingAI]);

  const buildContext = () => {
    if (!guide || !opportunity) return '';

    let context = `Background Opportunity and Customer Context: ${opportunity.ai_context}\n\n`;
    context += `Detailed Systems Engineer Work Log: ${opportunity.SE_Notes__c || 'No summary available.'}\n\n`;
    context += `Technical Sales Process Stage: ${opportunity.highest_se_phase || 'Unknown stage'}\n\n`;
    context += `Close Status: ${closeStatus}\n`;
    context += `Additional Notes: ${additionalNotes}\n\n`;
    context += `Opportunity Outcomes:\n\n`;

    guide.outcomes.forEach((outcome) => {
      context += `Outcome Title: ${outcome.title}\n`;
      if (outcome.description) {
        context += `Description: ${outcome.description}\n`;
      }
      context += `\nUse Cases:\n`;

      if (!outcome.useCases.length) {
        context += `  No use cases available for this outcome.\n`;
      } else {
        outcome.useCases.forEach((usecase) => {
          context += `  Use-Case Title: ${usecase.title}\n`;
          if (usecase.description) context += `  Description: ${usecase.description}\n`;
          context += `  Type: ${usecase.type}\n`;

          const product = products?.find((p) => p.id === usecase.product);
          if (product) context += `  Selected Product: ${product.name}\n`;

          if (!usecase.testCases.length) {
            context += `  No test cases available for this use case.\n`;
          } else {
            context += `  Test Cases:\n`;
            usecase.testCases.forEach((testCase) => {
              context += `    Test Case Title: ${testCase.title}\n`;
              if (testCase.description) context += `    Description: ${testCase.description}\n`;
              context += `    Success: ${testCase.successful ? 'Yes' : 'No'}\n`;
            });
          }

          context += `\n`;
        });
      }

      context += `\n`;
    });

    return context;
  };

  const setClosedStatus = async () => {
    if (!closeStatus || !opportunity) return;

    const seStatus =
      closeStatus === 'Won'
        ? OpportunitySEStatus.TechnicalClose
        : OpportunitySEStatus.TechnicalOnHold;

    const sePhase =
      closeStatus === 'Won'
        ? OpportunityPhase.TechCloseWon
        : OpportunityPhase.TechCloseLost;

    try {
      await patchItem('sf/opportunity', {
        Id: opportunity.Id,
        SE_Status__c: seStatus,
      });

      await updateOpportunity({
        id: opportunity.id || opportunity.Id,
        se_phase: sePhase,
        highest_se_phase: OpportunityPhase.TechClose,
        updated_by: email,
        init_config: true,
      });
    } catch (error) {
      console.error('Error setting technical close result:', error);
      throw error;
    }

    await updateSENote(
      opportunity,
      `Opportunity has been Technically ${closeStatus}${additionalNotes ? `. ${additionalNotes}` : ''}`
    );
  };

  const handleFormSubmit = async (
    event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLHbrButtonElement>
  ) => {
    event.preventDefault();

    if (isSubmitting || aiLoading) return;

    setIsSubmitting(true);
    setAIError(null);

    try {
      await setClosedStatus();

      const context = buildContext();
      setAiContextText(context);
      setAILoading(true);
      setAIResponse(null);

      let chat = await fetchOrCreateAIContent(
        'opportunity',
        opportunityId,
        104,
        context,
        '',
        [],
        opportunity
      );

      if (!chat || typeof chat !== 'object' || !('id' in chat)) {
        chat = await createAIResponse(context, 'opportunity', opportunityId, 104, '', []);
      }

      if (chat && typeof chat === 'object' && chat.id) {
        setAIResponse(chat);
        setForceFormView(false);
      } else {
        setAIError('No AI technical close summary could be created.');
      }
    } catch (error) {
      setAIError('Error fetching AI technical close summary.');
    } finally {
      setAILoading(false);
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setForceFormView(true);
    setAIResponse(null);
    setAiContextText('');
    setAIError(null);
  };

  return (
    <div className="hbr-css__layout-col-sm" style={{ marginTop: 24 }}>
      <HbrCard container={true} style={{ width: '100%', height: techCloseBoxHeight }}>
        <h3 slot="header" className="hbr-css__text-heading-lg-bold hbr-css__margin-none">
          Tech Close Phase
        </h3>

        <div className="hbr-css__container-empty" style={{ minHeight: '77px', padding: 24 }}>
          {shouldShowForm ? (
            <form onSubmit={handleFormSubmit}>
              <div style={{ maxWidth: 400, marginBottom: 24 }}>
                <HbrSelect
                  label="Close Status"
                  value={closeStatus}
                  onHbr-change={(e) => setCloseStatus(asSingleValue(e.target.value))}
                  disabled={isSubmitting || aiLoading}
                >
                  {closeStatusOptions.map((option) => (
                    <HbrOption key={`csOption-${option.value}-${option.label}`} value={option.value}>
                      {option.label}
                    </HbrOption>
                  ))}
                </HbrSelect>
              </div>

              <div style={{ marginBottom: 24 }}>
                <HbrTextarea
                  label="Additional Notes"
                  name="additionalNotes"
                  value={additionalNotes}
                  onHbr-input={(e) => setAdditionalNotes(String(e.target.value))}
                  rows={3}
                  style={{ width: '100%' }}
                  disabled={isSubmitting || aiLoading}
                />
              </div>

              <StbButton
                role="save"
                onClick={(e) => void handleFormSubmit(e)}
                disabled={isSubmitting || aiLoading}
                loading={isSubmitting || aiLoading}
              >
                Submit
              </StbButton>

              {aiError && (
                <Typography color="error" style={{ marginTop: 16 }}>
                  {aiError}
                </Typography>
              )}
            </form>
          ) : aiLoading ? (
            <div>
              <HbrSpinner size="large">
                <div slot="label">Loading...</div>
              </HbrSpinner>
            </div>
          ) : aiResponse ? (
            <>
              <AIContent
                related_type="opportunity"
                related_id={opportunityId}
                question_id={104}
                height={aiContentHeight}
                width="100%"
                conversation={[]}
                context={aiContextText}
                conversationRequired={false}
                item={opportunity}
                pdfTitle={`${opportunity?.Name} AI Tech Close`}
              />
              <StbButton
                onClick={handleReset}
                role="request"
                className="mt-6"
              >
                Back to Form
              </StbButton>
            </>
          ) : (
            <Typography color="error">No AI technical close summary found.</Typography>
          )}
        </div>
      </HbrCard>
    </div>
  );
};

export default TechClosePhase;